import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { extractText, pickExtractor } from '../../lib/extractText';
import { createResume, confirmResume, activeResume, retryParse } from '../../lib/jobsApi';
import type { Resume as ResumeRecord } from '../../lib/jobsApi';
import { ApiError } from '../../lib/api';
import { usePolling } from '../../lib/usePolling';
import { formatRelativeDate } from '../../lib/formatRelativeDate';

/*
 * Velaigal /job-seeker/resume — upload flow + parse status.
 *
 * The upload is a strict state machine: idle -> extracting -> uploading ->
 * confirming -> idle. Every failure path toasts a message and drops back to
 * idle so the "Choose file" control is interactive again.
 *
 *   extracting  — extractText() runs pdfjs/mammoth in the browser
 *   uploading   — raw PUT of the file bytes to the presigned B2 url
 *   confirming  — tells the server the object landed, kicking off server-side
 *                 parsing (parseStatus becomes 'pending')
 *
 * Once parseStatus is 'pending', <ResumePoller> re-fetches the active resume
 * every 3s until it flips to 'done' or 'failed'. It's a standalone
 * module-scope component (not inlined) so it can be keyed on resume.id —
 * usePolling stops itself permanently once its fn returns false, so a fresh
 * key is what lets polling resume for a later resume that goes pending again.
 */

const MAX_BYTES = 10 * 1024 * 1024;

const MIME_BY_KIND: Record<'pdf' | 'docx', string> = {
  pdf: 'application/pdf',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

type UploadState = 'idle' | 'extracting' | 'uploading' | 'confirming';

const UPLOAD_STATE_LABEL: Record<Exclude<UploadState, 'idle'>, string> = {
  extracting: 'Reading your resume…',
  uploading: 'Uploading…',
  confirming: 'Confirming…',
};

const PARSE_STATUS_LABEL: Record<ResumeRecord['parseStatus'], string> = {
  pending: 'Parsing…',
  done: 'Parsed',
  failed: 'Parsing failed',
};

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

const ResumeStatusChip: React.FC<{ status: ResumeRecord['parseStatus'] }> = ({ status }) => {
  const dotColor =
    status === 'done'
      ? 'var(--color-success)'
      : status === 'failed'
        ? 'var(--color-error)'
        : 'var(--color-saffron-press)';
  return (
    <span
      className="chip"
      style={
        status === 'failed'
          ? { backgroundColor: 'rgba(153, 27, 27, 0.08)', color: 'var(--color-error)' }
          : undefined
      }
    >
      <span
        aria-hidden="true"
        className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0"
        style={{ backgroundColor: dotColor }}
      />
      {PARSE_STATUS_LABEL[status]}
    </span>
  );
};

// Module-scope so identity is stable across the parent's re-renders — keying
// it on resume.id lets a later resume's 'pending' status get its own fresh
// usePolling effect (see file header comment).
const ResumePoller: React.FC<{ onTick: () => Promise<boolean> }> = ({ onTick }) => {
  usePolling(onTick, 3000);
  return null;
};

const Resume: React.FC = () => {
  const [resume, setResume] = useState<ResumeRecord | null>(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadNonce, setLoadNonce] = useState(0);
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [retrying, setRetrying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Initial load (and retry, via loadNonce) — cancelled-guarded so a fast
  // unmount (or a stale in-flight retry) can't setState after the component
  // is gone. A failed load must NOT fall through to the "You haven't
  // uploaded a resume yet" copy — that would misreport a load failure as an
  // empty state — so it renders a load-error message with retry instead.
  useEffect(() => {
    let cancelled = false;
    setPageLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const res = await activeResume();
        if (!cancelled) setResume(res.resume);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof ApiError ? err.message : 'Check your connection and try again.');
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadNonce]);

  // Used after a successful upload/retry, while the page is known to still
  // be mounted (it's called directly from a user-triggered handler).
  const fetchActive = useCallback(async () => {
    try {
      const res = await activeResume();
      setResume(res.resume);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load your resume.');
    }
  }, []);

  const pollTick = useCallback(async (): Promise<boolean> => {
    try {
      const res = await activeResume();
      setResume(res.resume);
      return res.resume?.parseStatus === 'pending';
    } catch {
      // Transient network hiccup — stay quiet and keep polling rather than
      // toasting on every failed background tick.
      return true;
    }
  }, []);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    e.target.value = ''; // allow re-selecting the same file after a failure
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast.error('That file is larger than 10MB.');
      return;
    }

    let kind: 'pdf' | 'docx';
    try {
      kind = pickExtractor(file.type, file.name);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Only PDF and DOCX resumes are supported.');
      return;
    }
    const mime = MIME_BY_KIND[kind];

    setUploadState('extracting');
    let text: string;
    try {
      text = await extractText(file);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not read that file.');
      setUploadState('idle');
      return;
    }

    setUploadState('uploading');
    let resumeId: string;
    let putUrl: string;
    try {
      const created = await createResume({ filename: file.name, mime, size: file.size });
      resumeId = created.resumeId;
      putUrl = created.putUrl;
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not start the upload.');
      setUploadState('idle');
      return;
    }

    try {
      // Only the content-type header — the browser sets content-length
      // itself, and the presigned url was signed against this exact mime.
      const putRes = await fetch(putUrl, {
        method: 'PUT',
        headers: { 'content-type': mime },
        body: file,
      });
      if (!putRes.ok) throw new Error('Upload failed.');
    } catch {
      toast.error('Could not upload the file. Check your connection and try again.');
      setUploadState('idle');
      return;
    }

    setUploadState('confirming');
    try {
      await confirmResume(resumeId, text);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not confirm the upload.');
      setUploadState('idle');
      return;
    }

    toast.success("Resume uploaded — we're parsing it now.");
    setUploadState('idle');
    await fetchActive();
  };

  const handleRetry = async () => {
    if (!resume || retrying) return;
    setRetrying(true);
    try {
      await retryParse(resume.id);
      toast.success('Retrying — this can take a minute.');
      await fetchActive();
    } catch (err) {
      // ApiError.message is already the server's message, including the
      // quota_exceeded copy ("Daily limit reached — try again tomorrow.") —
      // no special-casing needed, just don't swap it for a generic string.
      toast.error(err instanceof ApiError ? err.message : 'Could not retry parsing.');
    } finally {
      setRetrying(false);
    }
  };

  let cardBody: React.ReactNode;
  if (pageLoading) {
    cardBody = (
      <div role="status" className="card text-center py-12">
        <span aria-hidden="true" className="velaigal-loader-rule mx-auto w-12 block" />
        <p className="mt-4 text-sm text-ink-tertiary">Loading your resume.</p>
      </div>
    );
  } else if (loadError) {
    cardBody = (
      <div className="card text-center py-12">
        <span className="label-editorial" style={{ color: 'var(--color-error)' }}>
          Something went wrong
        </span>
        <p className="mt-4 text-sm text-ink-secondary">Couldn&apos;t load your resume. {loadError}</p>
        <button
          type="button"
          className="btn-secondary mt-6"
          onClick={() => setLoadNonce((n) => n + 1)}
        >
          Try again
        </button>
      </div>
    );
  } else if (resume) {
    cardBody = (
      <div className="card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <p className="text-ink font-medium truncate">{resume.originalFilename}</p>
            <p className="text-sm text-ink-tertiary mt-1">
              {formatBytes(resume.size)} · Uploaded {formatRelativeDate(resume.createdAt)}
            </p>
          </div>
          <ResumeStatusChip status={resume.parseStatus} />
        </div>

        {resume.parseStatus === 'done' && resume.parsed && resume.parsed.skills.length > 0 && (
          <div className="mt-5 pt-5 border-t border-hairline">
            <span className="label-editorial block mb-3">Skills we found</span>
            <div className="flex flex-wrap gap-2">
              {resume.parsed.skills.map((skill) => (
                <span key={skill} className="chip">
                  {skill}
                </span>
              ))}
            </div>
          </div>
        )}

        {resume.parseStatus === 'failed' && (
          <div className="mt-5 pt-5 border-t border-hairline">
            <p className="text-sm text-ink-secondary mb-4">
              We couldn&apos;t parse this resume automatically.
            </p>
            <button
              type="button"
              className="btn-secondary"
              onClick={() => void handleRetry()}
              disabled={retrying}
            >
              {retrying ? 'Retrying…' : 'Retry parsing'}
            </button>
          </div>
        )}
      </div>
    );
  } else {
    cardBody = (
      <div className="card text-center py-12">
        <p className="text-sm text-ink-tertiary">You haven&apos;t uploaded a resume yet.</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <span className="label-editorial block mb-3">Resume</span>
      <h1 className="font-display text-3xl font-medium text-ink tracking-tight mb-8">
        Your resume
      </h1>

      <div className="card mb-6">
        <span className="label-editorial block mb-3">
          {resume ? 'Replace your resume' : 'Upload your resume'}
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx"
          onChange={(e) => void handleFileChange(e)}
          className="hidden"
          aria-hidden="true"
          tabIndex={-1}
        />
        <button
          type="button"
          className="btn-primary"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadState !== 'idle'}
        >
          {uploadState === 'idle' ? (
            'Choose file'
          ) : (
            <>
              <span
                className="inline-block h-3.5 w-3.5 rounded-full border-2 animate-spin"
                style={{
                  borderColor: 'rgba(244, 240, 232, 0.35)',
                  borderTopColor: 'var(--color-cream)',
                }}
                aria-hidden="true"
              />
              <span>{UPLOAD_STATE_LABEL[uploadState]}</span>
            </>
          )}
        </button>
        <p className="text-xs text-ink-tertiary mt-3">PDF or DOCX, up to 10MB.</p>
      </div>

      {cardBody}

      {resume?.parseStatus === 'pending' && <ResumePoller key={resume.id} onTick={pollTick} />}
    </div>
  );
};

export default Resume;
